import { memo, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { groupByStatus } from '@shared/domain/order.ts';
import {
  STATUS_OPTIONS,
  findStatusOption,
  isClosedStatus,
  statusKey,
} from '@shared/domain/status.ts';
import { store, useApp } from '../state/store.ts';
import { formatFechas } from '../lib/format.ts';
import { peopleIndex, personInfo, type PeopleIndex } from '../lib/people.ts';
import type { CardListItem } from '../lib/types.ts';
import {
  Avatar,
  HoursButton,
  TagChip,
  usePullToRefresh,
  useWideLayout,
} from '../components/common.tsx';
import { Kanban } from '../components/Kanban.tsx';

type View = 'todo' | 'activo';
const VIEW_KEY = 'tablero.vista';
/**
 * Tarjetas por sección que se muestran de una vez. El alcance real tiene ~2700 tarjetas (casi
 * todas cerradas): pintarlas todas cuesta ~0,5 s en un teléfono; el resto se muestra a pedido.
 */
export const SECTION_PAGE = 100;

function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'activo' ? 'activo' : 'todo';
  } catch {
    return 'todo';
  }
}

interface CardProps {
  card: CardListItem;
  people: PeopleIndex;
  running: boolean;
}

// Memoizada y sin suscribirse al store: un cambio de estado ajeno a la tarjeta (avisos,
// sincronización, conexión) no vuelve a pintar la lista. Sin el <li>: la lista y el Kanban
// ponen el suyo.
const CardContent = memo(function CardContent({ card, people, running }: CardProps) {
  const name = card.name || '(sin nombre)';
  return (
    <>
      <Link
        to={`/tarjeta/${card.id}`}
        className={`card-item${running ? ' card-item-running' : ''}`}
      >
        <span className="card-title">{name}</span>
        <span className="card-meta">
          {card.etiquetas.map((t) => (
            <TagChip key={t} tag={t} />
          ))}
          {card.marca && <span className="chip chip-brand">{card.marca}</span>}
        </span>
        <span className="card-foot">
          <span className="muted small">{formatFechas(card.fechas)}</span>
          <span className="avatars">
            {card.miembros.map((id) => (
              <Avatar key={id} person={personInfo(people, id)} />
            ))}
          </span>
        </span>
        {running && <span className="card-running">⏱ En curso</span>}
      </Link>
      <HoursButton cardId={card.id} name={name} running={running} />
    </>
  );
});

function CardItem(props: CardProps) {
  return (
    <li className="card-row">
      <CardContent {...props} />
    </li>
  );
}

/** Todas las tarjetas: la vista Todo/Activo de v1, con «Solo lo mío», desde el menú de la cuenta. */
export function BoardPage() {
  const { cards, profile, notionUsers, people, syncing, openEntry } = useApp();
  const [view, setView] = useState<View>(readView);
  const [shown, setShown] = useState<Record<string, number>>({});
  const { handlers, hint } = usePullToRefresh();
  const wide = useWideLayout();
  const soloLoMio = profile?.solo_lo_mio ?? true;
  const me = profile?.notion_user_id ?? null;

  const runningId = openEntry?.card_id ?? null;
  const index = useMemo(() => peopleIndex(notionUsers, people), [notionUsers, people]);
  const sections = useMemo(() => {
    const visible = cards.filter(
      (c) =>
        (view === 'todo' || !c.is_closed) &&
        (!soloLoMio || (me !== null && c.miembros.includes(me))),
    );
    return groupByStatus(visible);
  }, [cards, view, soloLoMio, me]);

  function changeView(next: View) {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // almacenamiento no disponible
    }
  }

  // Kanban: una columna por opción de Status, también vacía para poder soltar en ella (en Activo,
  // sin la de cerradas), y al final las opciones que Tablero no conoce.
  const columns = useMemo(() => {
    const byKey = new Map(sections.map((s) => [statusKey(s.status), s]));
    const known = STATUS_OPTIONS.filter((o) => view === 'todo' || !isClosedStatus(o.name)).map(
      (o) => ({
        key: o.name,
        label: o.name,
        tone: `status-${o.group}`,
        cards: byKey.get(statusKey(o.name))?.cards ?? [],
      }),
    );
    const unknown = sections
      .filter((s) => !findStatusOption(s.status))
      .map((s) => ({ key: s.status, label: s.status, tone: `status-${s.group}`, cards: s.cards }));
    return [...known, ...unknown];
  }, [sections, view]);

  const total = sections.reduce((n, s) => n + s.cards.length, 0);

  return (
    <div {...handlers}>
      {hint}
      <h1>Todas las tarjetas</h1>
      <div className="toolbar">
        <div className="tabs" role="tablist" aria-label="Vista">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'todo'}
            onClick={() => changeView('todo')}
          >
            Todo
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'activo'}
            onClick={() => changeView('activo')}
          >
            Activo
          </button>
        </div>
        <label className="switch">
          <input
            type="checkbox"
            role="switch"
            checked={soloLoMio}
            onChange={(e) => void store.setSoloLoMio(e.target.checked)}
          />
          Solo lo mío
        </label>
      </div>
      <p className="muted small" data-testid="card-count">
        {total === 1 ? '1 tarjeta' : `${total} tarjetas`}
        {syncing ? ' · actualizando…' : ''}
      </p>
      {sections.length === 0 && !wide && (
        <p className="empty">
          {soloLoMio
            ? 'No tienes tarjetas asignadas en esta vista.'
            : 'No hay tarjetas en esta vista.'}
        </p>
      )}
      {wide && (
        <Kanban
          label="Tarjetas por Status"
          columns={columns}
          pageSize={SECTION_PAGE}
          cardName={(card) => card.name || '(sin nombre)'}
          renderCard={(card) => (
            <CardContent card={card} people={index} running={card.id === runningId} />
          )}
          onMove={(card, to) => void store.updateCard(card.id, { status: to })}
        />
      )}
      {!wide &&
        sections.map((section) => {
          const limit = shown[section.status] ?? SECTION_PAGE;
          const rest = section.cards.length - limit;
          return (
            <section
              key={section.status}
              className={`status-section status-${section.group}`}
              aria-label={section.status}
            >
              <h2>
                {section.status} <span className="count">{section.cards.length}</span>
              </h2>
              <ul className="card-list">
                {section.cards.slice(0, limit).map((card) => (
                  <CardItem
                    key={card.id}
                    card={card}
                    people={index}
                    running={card.id === runningId}
                  />
                ))}
              </ul>
              {rest > 0 && (
                <button
                  type="button"
                  className="button show-more"
                  onClick={() => setShown({ ...shown, [section.status]: limit + SECTION_PAGE })}
                >
                  Mostrar {Math.min(rest, SECTION_PAGE)} más (quedan {rest})
                </button>
              )}
            </section>
          );
        })}
    </div>
  );
}
